import { Spinner } from '@/components/glitch-ui/spinner';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Plus, Trash2, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { EmptyState } from '@/components/glitch-ui/empty-state';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { useToast } from '@/hooks/useToast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { linkCampaignConversionAction, unlinkCampaignConversionAction } from '@/lib/apis';
import { campaignConversionActionsQueryOptions, conversionActionsQueryOptions, useAccountConversionActionsReportQuery } from '@/lib/query-options';
import { dateWindow } from '@/lib/performance/date-window';
import { useCampaignDraftFields } from '@/hooks/useCampaignDraft';
import { campaignDraftStateAtom } from '@/store';
import {
  validateConversionEditInput,
  type CampaignConversionEdit,
} from '@/lib/conversion-draft';
import type { CampaignConversionLink } from '@/lib/types';
import type { ColumnDef } from '@tanstack/react-table';
import { CreateConversionForm } from '@/components/settings/create-conversion-form';

type ConversionLink = CampaignConversionLink;

interface ConversionTrackingTabProps {
  campaignId: number;
  disabled?: boolean;
}

export function ConversionTrackingTab({ campaignId, disabled = false }: ConversionTrackingTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { symbol } = useCurrencySymbol();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addDialogTab, setAddDialogTab] = useState<'existing' | 'google' | 'hubspot'>('existing');
  const [selectedActionIds, setSelectedActionIds] = useState<Set<number>>(new Set());
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; actionId: number; name: string }>({
    isOpen: false,
    actionId: 0,
    name: '',
  });

  const { data: links = [], isLoading } = useQuery(campaignConversionActionsQueryOptions(campaignId));

  // #6563 — conversions are shown for a selectable window instead of the fixed
  // 30-day snapshot on the action. `conversions_last_30d` is account-level (a
  // property of the conversion action, not this campaign), so we keep the same
  // account-level meaning and just make the window adjustable via the dated
  // per-action report. Default 30d == the previous view.
  const [rangeDays, setRangeDays] = useState<number>(30);
  const { start_date: rangeStart, end_date: rangeEnd } = useMemo(() => dateWindow(rangeDays), [rangeDays]);
  const { data: conversionReport } = useAccountConversionActionsReportQuery(rangeStart, rangeEnd);
  // Google Ads conversion-action id -> conversions in the selected window.
  // The report keys on the bare GA action id, which matches link.conversion_action.google_ads_action_id.
  const conversionsByActionId = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of conversionReport ?? []) {
      if (r.conversion_action_id == null) continue;
      m.set(r.conversion_action_id, (m.get(r.conversion_action_id) ?? 0) + (r.conversions ?? 0));
    }
    return m;
  }, [conversionReport]);

  // Row edits stage into the campaign draft (#6479) and persist via the page's Save
  // Changes button — nothing hits the backend from this modal. The baseline hydrates in
  // useCampaignDraft at the layout level (same query), so draft + original are in the atom.
  const draftState = useAtomValue(campaignDraftStateAtom);
  const { updateDraft } = useCampaignDraftFields();
  const stagedEntries = draftState.draft?.campaignId === campaignId ? draftState.draft.conversionActions : undefined;
  const baselineEntries =
    draftState.original?.campaignId === campaignId ? draftState.original.conversionActions : undefined;
  const stagedById = useMemo(
    () => new Map((stagedEntries ?? []).map((entry) => [entry.conversionActionId, entry])),
    [stagedEntries]
  );
  const baselineById = useMemo(
    () => new Map((baselineEntries ?? []).map((entry) => [entry.conversionActionId, entry])),
    [baselineEntries]
  );

  // Rows render the STAGED values so pending edits are visible before Save.
  const displayLinks = useMemo(
    () =>
      links.map((link) => {
        const staged = stagedById.get(link.conversion_action_id);
        if (!staged || !link.conversion_action) return link;
        return {
          ...link,
          conversion_action: {
            ...link.conversion_action,
            name: staged.name,
            funnel_stage: staged.funnelStage,
            default_value: staged.defaultValue,
            primary_for_goal: staged.primaryForGoal,
          },
        };
      }),
    [links, stagedById]
  );
  // Edit modal (#6479): seeded from the staged entry so reopening shows pending edits;
  // "Done" restages, Cancel/X discards the modal's changes. Tell-on-click validation.
  const [editingActionId, setEditingActionId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editStage, setEditStage] = useState('');
  const [editValueText, setEditValueText] = useState('');
  const [editPrimary, setEditPrimary] = useState('');
  const [editErrors, setEditErrors] = useState<{ name?: string; value?: string }>({});

  const editingLink = editingActionId !== null
    ? links.find((link) => link.conversion_action_id === editingActionId)
    : undefined;

  const openEditModal = (link: ConversionLink) => {
    if (disabled || !link.conversion_action) return;
    const staged = stagedById.get(link.conversion_action_id);
    // Baseline not hydrated yet (draft still initializing) — nothing to stage against.
    if (!staged) return;
    setEditName(staged.name);
    setEditStage(staged.funnelStage ?? '');
    setEditValueText(staged.defaultValue != null ? String(staged.defaultValue) : '');
    setEditPrimary(staged.primaryForGoal == null ? '' : staged.primaryForGoal ? 'primary' : 'secondary');
    setEditErrors({});
    setEditingActionId(link.conversion_action_id);
  };

  const closeEditModal = () => setEditingActionId(null);

  const handleEditDone = () => {
    if (editingActionId === null || stagedEntries === undefined) return;
    const baseline = baselineById.get(editingActionId);
    const errors = validateConversionEditInput({
      name: editName,
      valueText: editValueText,
      baselineHadValue: baseline?.defaultValue != null,
    });
    if (errors.name || errors.value) {
      setEditErrors(errors);
      return;
    }
    const trimmedValue = editValueText.trim();
    const next: CampaignConversionEdit = {
      conversionActionId: editingActionId,
      name: editName.trim(),
      funnelStage: editStage || null,
      defaultValue: trimmedValue === '' ? null : Number(trimmedValue),
      primaryForGoal: editPrimary === '' ? null : editPrimary === 'primary',
    };
    updateDraft(
      'conversionActions',
      stagedEntries.map((entry) => (entry.conversionActionId === editingActionId ? next : entry))
    );
    closeEditModal();
  };

  // Org/account-scoped options (#6450) — the bare ['conversion-actions'] key
  // this used previously could serve another org's actions after a switch.
  const { data: allActions } = useQuery({
    ...conversionActionsQueryOptions(),
    enabled: showAddDialog,
  });

  const availableActions = (allActions?.conversion_actions || []).filter(
    (action) =>
      action.registry_id != null &&
      !links.some((link: ConversionLink) => link.conversion_action_id === action.registry_id)
  );

  const linkMutation = useMutation({
    mutationFn: (ids: number[]) =>
      Promise.all(ids.map((id) => linkCampaignConversionAction(campaignId, id))),
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId, 'conversion-actions'] });
      setShowAddDialog(false);
      setSelectedActionIds(new Set());
      toast({
        title: 'Conversions Linked',
        description: `${ids.length} conversion action${ids.length > 1 ? 's' : ''} linked to campaign`,
      });
    },
    onError: (error) => {
      toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    },
  });

  const toggleAction = (id: number) => {
    setSelectedActionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const unlinkMutation = useMutation({
    mutationFn: (conversionActionId: number) =>
      unlinkCampaignConversionAction(campaignId, conversionActionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId, 'conversion-actions'] });
      setDeleteModal({ isOpen: false, actionId: 0, name: '' });
      toast({ title: 'Conversion Unlinked', description: 'Conversion action removed from campaign' });
    },
    onError: (error) => {
      toast({ title: 'Failed', description: error.message, variant: 'destructive' });
    },
  });

  const sourceConfig: Record<string, { label: string; icon: string }> = {
    GOOGLE_TAG: { label: 'Google Tag', icon: '/google-ads.svg' },
    HUBSPOT: { label: 'HubSpot', icon: '/hubspot.png' },
    META: { label: 'Meta', icon: '/meta.png' },
  };

  const formatConversionType = (type: string | null | undefined): string | null => {
    const labels: Record<string, string> = {
      WEBPAGE: 'Website',
      PHONE_CALL: 'Phone Call',
      UPLOAD_CLICKS: 'Offline',
      UPLOAD_CALLS: 'Offline Call',
    };
    return type ? (labels[type] ?? null) : null;
  };

  const stageConfig: Record<string, { label: string; className: string }> = {
    LEAD: { label: 'Lead', className: 'bg-gray-100 text-gray-900' },
    MQL: { label: 'MQL', className: 'bg-blue-100 text-blue-800' },
    SQL: { label: 'SQL', className: 'bg-amber-100 text-amber-800' },
    CUSTOMER: { label: 'Customer', className: 'bg-green-100 text-green-800' },
  };

  const baseColumns: ColumnDef<ConversionLink, unknown>[] = [
    {
      accessorKey: 'conversion_action.name',
      header: 'Name',
      cell: ({ row }) => {
        const action = row.original.conversion_action;
        return (
          <div>
            <span className="text-sm font-medium text-gray-900">{action?.name || '—'}</span>
            {formatConversionType(action?.action_type) && (
              <div className="text-sm font-normal text-gray-500">{formatConversionType(action?.action_type)}</div>
            )}
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.conversion_action?.source ?? '',
      id: 'source',
      header: 'Source',
      cell: ({ row }) => {
        const source = row.original.conversion_action?.source;
        if (source === 'PHONE_CALL') {
          return (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-900">Phone Call</span>
            </div>
          );
        }
        const config = source ? sourceConfig[source] : null;
        return config ? (
          <div className="flex items-center gap-2">
            <img src={config.icon} alt="" className="h-4 w-4 object-contain" />
            <span className="text-sm text-gray-900">{config.label}</span>
          </div>
        ) : (
          <span className="text-sm text-gray-500">Unknown</span>
        );
      },
    },
    {
      accessorFn: (row) => row.conversion_action?.funnel_stage ?? '',
      id: 'funnel_stage',
      header: 'Stage',
      cell: ({ row }) => {
        const stage = row.original.conversion_action?.funnel_stage;
        const config = stage ? stageConfig[stage] : null;
        return config ? (
          <span className={`inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
            {config.label}
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        );
      },
    },
    {
      accessorFn: (row) => row.conversion_action?.primary_for_goal ?? null,
      id: 'goal',
      header: 'Goal',
      cell: ({ row }) => {
        const primary = row.original.conversion_action?.primary_for_goal;
        return primary === true ? (
          <span className="inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Primary
          </span>
        ) : primary === false ? (
          <span className="inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-900">
            Secondary
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        );
      },
    },
    {
      accessorFn: (row) => row.goal_config_level ?? null,
      id: 'scope',
      header: 'Scope',
      cell: ({ row }) => {
        const isCampaignScoped = row.original.goal_config_level === 'CAMPAIGN';
        return isCampaignScoped ? (
          <span className="inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium text-[#290BD9] bg-[#EDE9FE]">
            Campaign
          </span>
        ) : (
          <span className="inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-900">
            Account
          </span>
        );
      },
    },
    {
      accessorFn: (row) => row.conversion_action?.default_value ?? null,
      id: 'value',
      header: 'Value',
      cell: ({ row }) => {
        const val = row.original.conversion_action?.default_value;
        return val != null ? (
          <span className="text-sm font-medium text-gray-900">
            {`${symbol}${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        );
      },
    },
    {
      accessorFn: (row) => {
        const gaId = row.conversion_action?.google_ads_action_id;
        return gaId ? (conversionsByActionId.get(gaId) ?? 0) : null;
      },
      id: 'conversions',
      header: 'Conv',
      cell: ({ row }) => {
        const gaId = row.original.conversion_action?.google_ads_action_id;
        const val = gaId ? conversionsByActionId.get(gaId) : undefined;
        return val != null && val > 0
          ? <span className="text-sm text-gray-900">{val.toLocaleString()}</span>
          : <span className="text-sm text-gray-400">—</span>;
      },
    },
    {
      id: 'actions',
      header: () => null,
      enableSorting: false,
      // Narrow, right-aligned; keeps the trash icon inside the table edge (#6479).
      meta: { className: 'pl-2 pr-4 w-16' },
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <IconButton
            variant="default"
            aria-label="Edit conversion"
            disabled={disabled}
            onClick={(e) => {
              // Same action as the row click - explicit affordance for discoverability.
              e.stopPropagation();
              openEditModal(row.original);
            }}
          >
            <Pencil className="h-4 w-4" />
          </IconButton>
          <IconButton
            variant="destructive"
            aria-label="Remove conversion"
            onClick={(e) => {
              // Row click opens the edit modal — don't let the delete affordance trigger it.
              e.stopPropagation();
              setDeleteModal({
                isOpen: true,
                actionId: row.original.conversion_action_id,
                name: row.original.conversion_action?.name || 'this action',
              });
            }}
          >
            <Trash2 className="h-4 w-4" />
          </IconButton>
        </div>
      ),
    },
  ];

  // The DataTable default of px-6 per cell overflows this seven-column table and
  // pushes the actions column past the edge (#6479) - tighten cells here only.
  const columns: ColumnDef<ConversionLink, unknown>[] = baseColumns.map((c) => ({
    meta: { className: 'px-3' },
    ...c,
  }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" className="text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Conversions</h2>
          <p className="text-sm text-gray-500 mt-1">Link conversion actions to track performance for this campaign.</p>
        </div>
        <div className="flex items-center gap-3">
          {/* #6563 — window for the Conv column. */}
          <div className="flex items-center gap-2">
            <Select value={String(rangeDays)} onValueChange={(v) => setRangeDays(Number(v))}>
              <SelectTrigger className="h-9 w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" className="text-gray-900" onClick={() => setShowAddDialog(true)} disabled={disabled}>
            <Plus className="h-4 w-4 mr-2" />
            Add Conversion
          </Button>
        </div>
      </div>

      {links.length > 0 ? (
        <DataTable
          data={displayLinks}
          columns={columns}
          variant="flush"
          pageSize={9999}
          getRowId={(row) => String(row.conversion_action_id)}
          onRowClick={disabled ? undefined : openEditModal}
        />
      ) : (
        <EmptyState
          icon={<Plus className="h-12 w-12 text-gray-400" strokeWidth={1} />}
          title="No Conversions Linked"
          description="Link conversion actions to this campaign to track ROAS"
        />
      )}

      {/* Add Conversion Dialog */}
      <Dialog
        open={showAddDialog}
        onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) {
            setSelectedActionIds(new Set());
            setAddDialogTab('existing');
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Conversion</DialogTitle>
          </DialogHeader>

          {/* Source tabs */}
          <div className="flex gap-4 border-b border-gray-200">
            <button
              onClick={() => setAddDialogTab('existing')}
              className={cn(
                'px-1 pb-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                addDialogTab === 'existing'
                  ? 'border-[#290BD9] text-[#290BD9]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              Existing
            </button>
            <button
              onClick={() => setAddDialogTab('google')}
              className={cn(
                'flex items-center gap-2 px-1 pb-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                addDialogTab === 'google'
                  ? 'border-[#290BD9] text-[#290BD9]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <img src="/google-ads.svg" alt="" className="h-4 w-4" />
              Google Tag
            </button>
            <button
              onClick={() => setAddDialogTab('hubspot')}
              className={cn(
                'flex items-center gap-2 px-1 pb-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                addDialogTab === 'hubspot'
                  ? 'border-[#290BD9] text-[#290BD9]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <img src="/hubspot.png" alt="" className="h-4 w-4" />
              HubSpot
            </button>
          </div>

          {addDialogTab === 'existing' ? (
            <>
              <div className="py-2 min-h-[360px]">
                {availableActions.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">
                    All conversion actions are already linked to this campaign.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                    {availableActions.map((action) => (
                      <label
                        key={action.registry_id}
                        className="flex items-center gap-4 p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={selectedActionIds.has(action.registry_id!)}
                          onCheckedChange={() => toggleAction(action.registry_id!)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900">{action.name}</div>
                          {action.funnel_stage && (
                            <div className="text-xs text-gray-500 mt-0.5">{action.funnel_stage}</div>
                          )}
                        </div>
                        {action.source && (
                          <img
                            src={action.source === 'HUBSPOT' ? '/hubspot.png' : '/google-ads.svg'}
                            alt=""
                            className="h-4 w-4 shrink-0"
                          />
                        )}
                      </label>
                    ))}
                  </div>
                )}
                {availableActions.length > 0 && (
                  <p className="text-xs text-gray-500 text-center mt-3">
                    {selectedActionIds.size} of {availableActions.length} selected
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddDialog(false);
                    setSelectedActionIds(new Set());
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => linkMutation.mutate(Array.from(selectedActionIds))}
                  disabled={selectedActionIds.size === 0 || linkMutation.isPending}
                  className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
                >
                  {linkMutation.isPending ? 'Linking...' : `Link${selectedActionIds.size > 0 ? ` ${selectedActionIds.size}` : ''}`}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <CreateConversionForm
              key={addDialogTab}
              showSourceTabs={false}
              defaultTab={addDialogTab}
              onCreated={(registryIds) => {
                linkMutation.mutate(registryIds);
                setAddDialogTab('existing');
              }}
              onCancel={() => setShowAddDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Conversion Dialog (#6479) — Done stages the edits into the campaign draft;
          the page's Save Changes persists them. Cancel/X discards this modal's changes. */}
      <Dialog open={editingActionId !== null} onOpenChange={(open) => !open && closeEditModal()}>
        <DialogContent className="sm:max-w-[500px]" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingLink?.conversion_action?.source === 'HUBSPOT' && (
                <img src="/hubspot.png" alt="" className="h-5 w-5" />
              )}
              {editingLink?.conversion_action?.source === 'GOOGLE_TAG' && (
                <img src="/google-ads.svg" alt="" className="h-5 w-5" />
              )}
              Edit Conversion
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="conversion-edit-name">Name</Label>
                <FieldError message={editErrors.name} />
              </div>
              <div className="relative">
                <Input
                  id="conversion-edit-name"
                  value={editName}
                  error={!!editErrors.name}
                  className={editErrors.name ? 'pr-8' : undefined}
                  onChange={(e) => {
                    setEditName(e.target.value);
                    setEditErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  placeholder="Conversion name"
                />
                <InputAdornment error={!!editErrors.name} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="conversion-edit-stage">Stage</Label>
                <Select value={editStage} onValueChange={setEditStage}>
                  <SelectTrigger id="conversion-edit-stage">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LEAD">Lead</SelectItem>
                    <SelectItem value="MQL">MQL</SelectItem>
                    <SelectItem value="SQL">SQL</SelectItem>
                    <SelectItem value="CUSTOMER">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="conversion-edit-value">Conversion Value</Label>
                  <FieldError message={editErrors.value} />
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{symbol}</span>
                  <Input
                    id="conversion-edit-value"
                    type="number"
                    step="0.01"
                    min="0"
                    value={editValueText}
                    error={!!editErrors.value}
                    onChange={(e) => {
                      setEditValueText(e.target.value);
                      setEditErrors((prev) => ({ ...prev, value: undefined }));
                    }}
                    placeholder="0.00"
                    className={cn('pl-7', editErrors.value && 'pr-8')}
                  />
                  <InputAdornment error={!!editErrors.value} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="conversion-edit-primary">Conversion Type</Label>
                <Select value={editPrimary} onValueChange={setEditPrimary}>
                  <SelectTrigger id="conversion-edit-primary">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button onClick={handleEditDone} className="bg-[#290BD9] hover:bg-[#2309C5] text-white">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConformationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, actionId: 0, name: '' })}
        onConfirm={() => unlinkMutation.mutate(deleteModal.actionId)}
        title="Remove Conversion Link"
        description={`Remove "${deleteModal.name}" from this campaign? This won't delete the conversion action itself.`}
        confirmText="Remove"
        cancelText="Cancel"
      />
    </div>
  );
}
